/** @jsxImportSource @emotion/react */
'use client';

import React, { type KeyboardEvent, type MouseEvent } from 'react';
import { ComponentSize } from '../../types';
import {
  ButtonContent,
  ButtonInner,
  IconSlot,
  Spinner,
  SrOnly,
  StyledButton,
} from './styled';
import type { ButtonProps } from './types';

function blockDisabledActivation(e: MouseEvent | KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = ComponentSize.Medium,
      fullWidth = false,
      disabled = false,
      loading = false,
      loadingLabel = 'Loading',
      underline = false,
      active = false,
      leftIcon,
      rightIcon,
      type = 'button',
      as,
      href,
      onClick,
      className,
      testId = 'button',
      css,
      style,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const isAnchor = as === 'a' || Boolean(href);
    const resolvedHref = isAnchor && !isDisabled ? href : isAnchor ? undefined : href;
    const restRecord = rest as Record<string, unknown>;
    const hasAriaCurrent = restRecord['aria-current'] != null;
    const hasExplicitAriaPressed = 'aria-pressed' in restRecord;
    const shouldSetAriaPressed =
      (variant === 'chip' || variant === 'tab') &&
      !hasAriaCurrent &&
      !hasExplicitAriaPressed;

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        blockDisabledActivation(e);
        return;
      }
      onClick?.(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (!isDisabled || !isAnchor) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        blockDisabledActivation(e);
      }
    };

    return (
      <StyledButton
        ref={ref}
        as={as ?? (href ? 'a' : undefined)}
        href={resolvedHref}
        onClick={handleClick as any}
        onKeyDown={handleKeyDown as any}
        disabled={isAnchor ? undefined : isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        aria-pressed={shouldSetAriaPressed ? active : undefined}
        tabIndex={isAnchor && isDisabled ? -1 : undefined}
        type={isAnchor ? undefined : type}
        data-testid={testId}
        data-variant={variant}
        data-loading={loading || undefined}
        data-active={active || undefined}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $underline={underline}
        $loading={loading}
        $active={active}
        className={className}
        css={css}
        style={style}
        {...rest}
      >
        <ButtonInner>
          {loading ? <Spinner aria-hidden /> : null}
          {loading ? <SrOnly>{loadingLabel}</SrOnly> : null}
          <ButtonContent $hidden={loading} aria-hidden={loading || undefined}>
            {leftIcon ? <IconSlot aria-hidden="true">{leftIcon}</IconSlot> : null}
            {children}
            {rightIcon ? <IconSlot aria-hidden="true">{rightIcon}</IconSlot> : null}
          </ButtonContent>
        </ButtonInner>
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';
